import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { invitationService, type InvitationDetails } from '@services/invitationService';
import { useAuthStore } from '@store/index';
import { useHomeDestination } from '@hooks/useHomeDestination';
import { apiErrorStatus, toDisplayError } from '../utils/apiError';
import { useSingleUseToken } from '../utils/singleUseToken';
import {
  clearPendingInvitationToken,
  readPendingInvitationToken,
  stashPendingInvitationToken,
} from '../utils/pendingInvitationToken';

/**
 * REV-521: the landing page for an invitation email.
 *
 * The email links to /invites/accept#token=… (emailService.sendInvitationEmail;
 * REV-878 moved the token from the path into the fragment). Deliberately NOT
 * behind ProtectedRoute: POST /v1/invites/lookup is public so a signed-out
 * recipient can see who invited them before deciding to sign in, while
 * POST /v1/invites/accept requires auth.
 */
export default function AcceptInvitationPage() {
  const { t } = useTranslation();
  // From the URL fragment on arrival, or handed back after a sign-in round trip.
  // Read without removing: StrictMode runs an initializer twice.
  const tokenFromUrl = useSingleUseToken();
  const [token] = useState(() => tokenFromUrl ?? readPendingInvitationToken());
  useEffect(() => {
    clearPendingInvitationToken();
  }, []);
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const currentUser = useAuthStore((s) => s.user);
  const home = useHomeDestination();

  const [details, setDetails] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState<{ organizationName: string; role: string } | null>(null);

  // Accepting unmounts the focused button, so focus would fall to <body> and
  // the injected status region is not reliably announced. Move focus to the
  // result so keyboard and screen-reader users get the outcome and keep their
  // place in the tab order.
  const resultRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (accepted || loadError) resultRef.current?.focus();
  }, [accepted, loadError]);

  const load = useCallback(async () => {
    if (!token) {
      // Opened without a token, or reloaded after the page took it out of the
      // URL: the same outcome as an unknown token.
      setLoadError(t('invites.errors.notFound'));
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      setDetails(await invitationService.getByToken(token));
      // A signed-out recipient has to sign in before accepting, and may leave
      // for it by any route — the buttons below, or the AuthLayout logo, which
      // links to /login. Park the token now so every one of those finds it;
      // it expires on its own and logout removes it.
      if (!useAuthStore.getState().isAuthenticated) {
        stashPendingInvitationToken(token);
      }
    } catch (err) {
      const status = apiErrorStatus(err);
      if (status === 404 || status === 400) {
        // 400 = the token failed the route's length check, i.e. a mangled
        // link. To the reader that is the same thing as an invalid token —
        // don't surface the raw "Validation failed".
        setLoadError(t('invites.errors.notFound'));
      } else if (status === 410) {
        setLoadError(t('invites.errors.expired'));
      } else {
        setLoadError(toDisplayError(err, t('invites.errors.loadFailed')));
      }
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    setAcceptError(null);
    try {
      const result = await invitationService.accept(token);
      setAccepted({
        organizationName: result.organization_name,
        // accept() only returns the role SLUG; prefer the display name we
        // already resolved so the confirmation matches the card above it.
        role: details?.role?.name ?? result.role,
      });
    } catch (err) {
      const status = apiErrorStatus(err);
      if (status === 403) {
        // The invite is bound to a specific email address.
        setAcceptError(
          t('invites.errors.wrongEmail', {
            invited: details?.email ?? '',
            current: currentUser?.email ?? '',
          })
        );
      } else if (status === 409) {
        setAcceptError(t('invites.errors.alreadyMember'));
      } else if (status === 410) {
        setAcceptError(t('invites.errors.expired'));
      } else if (status === 401) {
        // The session lapsed: the API client is about to send the user to
        // sign in, and back to this page without the token.
        stashPendingInvitationToken(token);
        setAcceptError(t('invites.errors.signInRequired'));
      } else {
        setAcceptError(toDisplayError(err, t('invites.errors.acceptFailed')));
      }
    } finally {
      setAccepting(false);
    }
  };

  const shell = (children: React.ReactNode) => (
    <Box>
      <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
        {t('invites.title')}
      </Typography>
      {children}
    </Box>
  );

  if (loading) {
    return shell(
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label={t('common.loading')} />
      </Box>
    );
  }

  if (loadError) {
    return shell(
      <>
        <Alert severity="error" ref={resultRef} tabIndex={-1} sx={{ mb: 2 }}>
          {loadError}
        </Alert>
        {/* REV-939 review: "/" is a signed-in route, so signed out this goes
            to sign-in and says so. */}
        <Link component={RouterLink} to={home.to}>
          {home.signedIn ? t('invites.backHome') : t('common.signIn')}
        </Link>
      </>
    );
  }

  if (accepted) {
    return shell(
      <>
        <Alert severity="success" role="status" ref={resultRef} tabIndex={-1} sx={{ mb: 2 }}>
          {t('invites.accepted', {
            org: accepted.organizationName,
            role: accepted.role,
          })}
        </Alert>
        <Button component={RouterLink} to="/orgs" variant="contained">
          {t('invites.goToOrganizations')}
        </Button>
      </>
    );
  }

  const orgName = details?.organization?.name ?? t('invites.unknownOrg');
  const roleName = details?.role?.name ?? details?.role?.slug ?? t('invites.unknownRole');

  return shell(
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" component="h2" gutterBottom>
          {t('invites.heading', { org: orgName })}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {t('invites.subtitle', { org: orgName, role: roleName })}
        </Typography>

        <Divider sx={{ my: 2 }} />
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{t('invites.fields.email')}:</strong> {details?.email}
          </Typography>
          <Typography variant="body2">
            <strong>{t('invites.fields.role')}:</strong> {roleName}
          </Typography>
        </Stack>

        {acceptError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {acceptError}
          </Alert>
        )}

        {isAuthenticated ? (
          <Button
            variant="contained"
            onClick={handleAccept}
            // Never pre-disabled — only while the request is in flight.
            disabled={accepting}
            startIcon={accepting ? <CircularProgress size={16} /> : undefined}
          >
            {accepting ? t('invites.accepting') : t('invites.accept')}
          </Button>
        ) : (
          <>
            {/* Static guidance, not an urgent event — MUI Alert defaults to
                role="alert", which would interrupt assertively when it
                replaces the spinner. */}
            <Alert severity="info" role="status" sx={{ mb: 2 }}>
              {t('invites.signInPrompt')}
            </Alert>
            <Stack direction="row" spacing={2}>
              {/* Preserve the return path so the user lands back here. */}
              <Button
                component={RouterLink}
                to="/login"
                state={{ from: location }}
                onClick={() => stashPendingInvitationToken(token)}
                variant="contained"
              >
                {t('invites.signIn')}
              </Button>
              {/* `outlined`, not the default text variant: as a bare text
                  button this renders as a link with no underline, background
                  or border, which our own engine flags (CSS-16). */}
              <Button
                component={RouterLink}
                to="/register"
                state={{ from: location }}
                onClick={() => stashPendingInvitationToken(token)}
                variant="outlined"
              >
                {t('invites.createAccount')}
              </Button>
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}
